import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import {
  PackageIcon, SearchIcon, MapPinIcon, TruckIcon, EditIcon, CheckIcon,
  LayoutGridIcon, RowsIcon, FileSpreadsheetIcon,
} from "lucide-react";

import ContainerEditModal from "@/components/ContainerEditModal";
import { useAuth } from "@/context/AuthContext";

export default function Estoque() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [items, setItems] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [filterProduct, setFilterProduct] = useState("ALL");
  const [filterLote, setFilterLote] = useState("");
  const [selected, setSelected] = useState({});
  const [editing, setEditing] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [view, setView] = useState("table");
  const [dispatchForm, setDispatchForm] = useState({ no_pedido: "", nf_produto: "", cliente: "", motorista: "", cpf_motorista: "", placa: "" });
  const [busy, setBusy] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // container sendo editado (admin only)

  const load = async () => {
    const { data } = await api.get("/containers", { params: { status: "CHEIO" } });
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const products = useMemo(() => Array.from(new Set(items.map((i) => i.nome_produto).filter(Boolean))), [items]);

  const filtered = items.filter((c) => {
    if (filterText && !c.no_serie.toLowerCase().includes(filterText.toLowerCase())) return false;
    if (filterProduct !== "ALL" && c.nome_produto !== filterProduct) return false;
    if (filterLote && !(c.no_lote || "").toLowerCase().includes(filterLote.toLowerCase())) return false;
    return true;
  });

  const toggleSelect = (no_serie) =>
    setSelected((s) => {
      const wasSelected = !!s[no_serie];
      if (!wasSelected) {
        const currentCount = Object.values(s).filter(Boolean).length;
        if (currentCount >= 30) {
          toast.warning("Atenção: capacidade máxima por carreta é de 30 itens (tanques + refis).");
          return s;
        }
      }
      return { ...s, [no_serie]: !wasSelected };
    });

  const selectedList = Object.keys(selected).filter((k) => selected[k]);
  const selCount = selectedList.length;
  const capacityClass =
    selCount === 0 ? "bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85"
    : selCount <= 15 ? "bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85"
    : selCount <= 25 ? "bg-[#FFD60A] text-black hover:bg-[#ffdf3a]"
    : "bg-[#FF9F0A] text-black hover:bg-[#FF9F0A]/85";
  const capacityHint =
    selCount === 0 ? "Selecione 1 a 30 itens"
    : selCount <= 15 ? `${selCount} de até 30 itens`
    : selCount <= 25 ? `${selCount} de 30 — carreta cheia em breve`
    : `${selCount} de 30 — limite máximo`;

  const saveLocal = async (no_serie) => {
    try {
      await api.patch(`/containers/${no_serie}/local`, { local_estoque: editing[no_serie] });
      toast.success("Local atualizado");
      setEditing((e) => { const c = { ...e }; delete c[no_serie]; return c; });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const despachar = async () => {
    if (selectedList.length === 0) return;
    if (!dispatchForm.cliente || !dispatchForm.no_pedido || !dispatchForm.nf_produto ||
        !dispatchForm.motorista || !dispatchForm.cpf_motorista || !dispatchForm.placa) {
      return toast.error("Preencha todos os campos do manifesto");
    }
    setBusy(true);
    try {
      for (const no_serie of selectedList) {
        await api.post("/expedicao", { ...dispatchForm, no_serie });
      }
      toast.success(`Manifesto emitido · ${selectedList.length} contentor(es) → EM TRÂNSITO`);
      setSelected({});
      setOpenModal(false);
      setDispatchForm({ no_pedido: "", nf_produto: "", cliente: "", motorista: "", cpf_motorista: "", placa: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const exportExcel = () => {
    const rows = filtered.map((c) => ({
      "N° Série": c.no_serie,
      "Tipo": c.tipo_armazenamento,
      "Capacidade (L)": c.capacidade,
      "Código Produto": c.codigo_produto || "",
      "Produto": c.nome_produto || "",
      "Função Química": c.funcao_quimica || "",
      "Qtd Envasada (L)": c.qtd_envasado || "",
      "N° Lote": c.no_lote || "",
      "Data Validade": c.data_validade_lote || "",
      "Lacres": (c.lacres || []).join(", "),
      "Local Estoque": c.local_estoque || "",
      "GPS ID": c.gps_id || "",
      "GPS Lat": c.gps_lat,
      "GPS Lng": c.gps_lng,
      "Usuário Envase": c.usuario_envase || "",
      "Remessa Origem": c.remessa_origem || "",
      "Data Envase": c.data_envase || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cheios");
    const ts = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `estoque-cheios-${ts}.xlsx`);
    toast.success(`Exportado ${rows.length} registros para Excel`);
  };

  return (
    <div className="p-4 md:p-8 max-w-[1700px] mx-auto">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-1">Estoque · Pátio</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-none">Contentores Cheios</h1>
          <p className="text-sm text-muted-foreground mt-2">Visão em tempo real de todos os ativos envasados aguardando expedição.</p>
        </div>
        <PackageIcon className="w-12 h-12 text-[#FFD60A]" strokeWidth={1.2} />
      </header>

      {/* Filters */}
      <Card className="bg-[#14151A] border-border rounded-none p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Buscar N° Série</Label>
            <div className="relative mt-1">
              <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="est-filter-serie" value={filterText} onChange={(e) => setFilterText(e.target.value.toUpperCase())} placeholder="TQ-5000-... (bipa ou digite)" autoFocus className="pl-8 rounded-none bg-[#0A0A0C] font-mono h-10" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Produto</Label>
            <Select value={filterProduct} onValueChange={setFilterProduct}>
              <SelectTrigger data-testid="est-filter-produto" className="mt-1 rounded-none bg-[#0A0A0C] h-10 border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#14151A] border-border rounded-none">
                <SelectItem value="ALL">Todos os produtos</SelectItem>
                {products.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Lote</Label>
            <Input data-testid="est-filter-lote" value={filterLote} onChange={(e) => setFilterLote(e.target.value)} placeholder="L260..." className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" />
          </div>

          {/* View toggle + Export + Despachar */}
          <div className="md:col-span-2 flex items-end gap-2 justify-end">
            <div className="flex border border-border">
              <button
                data-testid="view-cards"
                onClick={() => setView("cards")}
                className={`px-3 h-10 flex items-center gap-1 ${view === "cards" ? "bg-[#00E5FF] text-black" : "text-muted-foreground hover:text-white"}`}
              >
                <LayoutGridIcon className="w-4 h-4" /> <span className="text-xs uppercase">Cards</span>
              </button>
              <button
                data-testid="view-table"
                onClick={() => setView("table")}
                className={`px-3 h-10 flex items-center gap-1 ${view === "table" ? "bg-[#00E5FF] text-black" : "text-muted-foreground hover:text-white"}`}
              >
                <RowsIcon className="w-4 h-4" /> <span className="text-xs uppercase">Tabela</span>
              </button>
            </div>
            <Button
              data-testid="export-excel"
              onClick={exportExcel}
              className="h-10 px-4 rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider text-xs"
            >
              <FileSpreadsheetIcon className="w-4 h-4 mr-1" /> Excel
            </Button>
            <div className="flex flex-col items-end">
              <Button
                onClick={() => setOpenModal(true)}
                disabled={selectedList.length === 0}
                data-testid="est-despachar"
                className={`rounded-none h-10 px-4 font-bold uppercase tracking-wider transition-colors ${capacityClass}`}
              >
                <TruckIcon className="w-4 h-4 mr-1" /> Despachar ({selCount})
              </Button>
              <div data-testid="capacity-hint" className={`text-[10px] uppercase tracking-widest mt-1 ${selCount >= 4 ? "text-[#FFD60A]" : "text-muted-foreground"}`}>{capacityHint}</div>
            </div>
          </div>
        </div>
      </Card>

      {filtered.length === 0 && (
        <Card className="bg-[#14151A] border-border rounded-none p-8 text-center text-muted-foreground">
          Nenhum contentor cheio. Conclua envases na Tela 03.
        </Card>
      )}

      {/* Table view */}
      {view === "table" && filtered.length > 0 && (
        <Card className="bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0C] text-[10px] uppercase tracking-widest text-muted-foreground sticky top-0">
                <tr>
                  <th className="px-3 py-3 w-8"></th>
                  <th className="text-left px-3 py-3">N° Série</th>
                  <th className="text-left px-3 py-3">Produto / Função Química</th>
                  <th className="text-right px-3 py-3">Qtd</th>
                  <th className="text-left px-3 py-3">N° Lote</th>
                  <th className="text-left px-3 py-3">Validade</th>
                  <th className="text-left px-3 py-3">Lacres</th>
                  <th className="text-left px-3 py-3">Local Estoque</th>
                  <th className="text-left px-3 py-3">GPS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const sel = !!selected[c.no_serie];
                  const isEditing = editing[c.no_serie] !== undefined;
                  return (
                    <tr
                      key={c.no_serie}
                      className={`border-t border-border/60 hover:bg-[#1C1D24] ${sel ? "bg-[#00E5FF]/5" : ""}`}
                      data-testid={`est-row-${c.no_serie}`}
                    >
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={sel}
                          onCheckedChange={() => toggleSelect(c.no_serie)}
                          data-testid={`est-check-${c.no_serie}`}
                          className="rounded-none border-border data-[state=checked]:bg-[#00E5FF] data-[state=checked]:text-black"
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-[#FFD60A] font-bold">{c.no_serie}<div className="text-[10px] text-muted-foreground font-sans">{c.tipo_armazenamento} · {c.capacidade}L</div></td>
                      <td className="px-3 py-2">
                        <div>{c.nome_produto}</div>
                        <div className="text-[11px] text-[#00E5FF] font-mono">
                          {c.codigo_produto && <span className="text-[#FFD60A] mr-1">#{c.codigo_produto}</span>}
                          {c.funcao_quimica}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[#00E5FF]">{c.qtd_envasado} L</td>
                      <td className="px-3 py-2 font-mono">{c.no_lote}</td>
                      <td className="px-3 py-2 font-mono">{c.data_validade_lote}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(c.lacres || []).map((l, i) => (
                            <span key={i} className="font-mono text-[10px] px-1 py-0.5 bg-[#FF9F0A]/15 text-[#FF9F0A] border border-[#FF9F0A]/40">{l}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <div className="flex gap-1">
                            <Input
                              value={editing[c.no_serie]}
                              onChange={(e) => setEditing((s) => ({ ...s, [c.no_serie]: e.target.value }))}
                              data-testid={`est-local-input-${c.no_serie}`}
                              className="rounded-none bg-[#0A0A0C] font-mono h-7 text-xs w-32"
                            />
                            <Button size="sm" onClick={() => saveLocal(c.no_serie)} data-testid={`est-local-save-${c.no_serie}`} className="rounded-none bg-[#FFD60A] text-black h-7 w-7 p-0">
                              <CheckIcon className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditing((s) => ({ ...s, [c.no_serie]: c.local_estoque || "" }))}
                            data-testid={`est-local-edit-${c.no_serie}`}
                            className="flex items-center gap-1 font-mono text-xs hover:text-[#FFD60A]"
                          >
                            <MapPinIcon className="w-3 h-3 text-muted-foreground" />
                            {c.local_estoque || "—"}
                            <EditIcon className="w-2.5 h-2.5 text-muted-foreground ml-1" />
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <span>{c.gps_id}</span>
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditTarget(c)}
                              data-testid={`est-editar-${c.no_serie}`}
                              className="h-6 w-6 p-0 hover:bg-[#FFD60A]/20"
                              title={isAdmin ? "Edição avançada (ADMIN)" : "Editar contentor (GESTOR)"}
                            >
                              <EditIcon className="w-3 h-3 text-[#FFD60A]" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Modal de edição avançada (admin) */}
      <ContainerEditModal
        open={!!editTarget}
        container={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => load()}
      />

      {/* Cards view */}
      {view === "cards" && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const sel = !!selected[c.no_serie];
            const isEditing = editing[c.no_serie] !== undefined;
            return (
              <Card
                key={c.no_serie}
                data-testid={`est-card-${c.no_serie}`}
                className={`bg-[#14151A] border-border rounded-none p-5 border-l-2 ${sel ? "border-l-[#00E5FF] bg-[#00E5FF]/5" : "border-l-[#FFD60A]"} transition-colors hover:bg-[#1C1D24]`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-mono text-2xl font-bold text-[#FFD60A]">{c.no_serie}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{c.tipo_armazenamento} · {c.capacidade}L</div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={sel} onCheckedChange={() => toggleSelect(c.no_serie)} data-testid={`est-check-card-${c.no_serie}`} className="rounded-none border-border data-[state=checked]:bg-[#00E5FF] data-[state=checked]:text-black" />
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Selecionar</span>
                  </label>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <div className="font-display text-base font-bold leading-tight">{c.nome_produto}</div>
                    {c.funcao_quimica && (
                      <div className="text-xs text-[#00E5FF] font-mono mt-0.5">
                        {c.codigo_produto && <span className="text-[#FFD60A] mr-2">#{c.codigo_produto}</span>}
                        {c.funcao_quimica}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/60">
                    <Info label="Qtd Envasada" value={`${c.qtd_envasado} L`} accent />
                    <Info label="Validade" value={c.data_validade_lote} mono />
                    <Info label="Lote" value={c.no_lote} mono accent />
                    <Info label="GPS" value={c.gps_id} mono />
                  </div>
                  <div className="pt-2 border-t border-border/60">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Lacres</div>
                    <div className="flex flex-wrap gap-1">
                      {(c.lacres || []).map((l, i) => (
                        <Badge key={i} className="rounded-none border bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/40 font-mono text-[11px]">{l}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="pt-2 border-t border-border/60">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                      <MapPinIcon className="w-3 h-3" /> Local no estoque
                    </div>
                    {isEditing ? (
                      <div className="flex gap-2">
                        <Input value={editing[c.no_serie]} onChange={(e) => setEditing((s) => ({ ...s, [c.no_serie]: e.target.value }))} className="rounded-none bg-[#0A0A0C] font-mono h-9 text-sm" />
                        <Button size="sm" onClick={() => saveLocal(c.no_serie)} className="rounded-none bg-[#FFD60A] text-black h-9"><CheckIcon className="w-4 h-4" /></Button>
                      </div>
                    ) : (
                      <button onClick={() => setEditing((s) => ({ ...s, [c.no_serie]: c.local_estoque || "" }))} className="flex items-center gap-2 text-sm font-mono text-white hover:text-[#FFD60A]">
                        {c.local_estoque || "— sem local —"} <EditIcon className="w-3 h-3 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dispatch modal */}
      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-3xl" data-testid="dispatch-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">Registrar Manifesto de Expedição</DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">
              {selectedList.length} contentor(es) · Data/Hora de saída: <span className="font-mono text-[#00E5FF]">{new Date().toLocaleString("pt-BR")}</span> <span className="text-[10px]">(gerado automaticamente na confirmação)</span>
            </div>
          </DialogHeader>
          <div className="mb-3 flex flex-wrap gap-1">
            {selectedList.map((n) => (
              <Badge key={n} className="rounded-none border bg-[#FFD60A]/15 text-[#FFD60A] border-[#FFD60A]/40 font-mono">{n}</Badge>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF]">Dados do Cliente e Carga</div>
              <ModalField label="Cliente de Destino" testId="m-cliente" value={dispatchForm.cliente} onChange={(v) => setDispatchForm({ ...dispatchForm, cliente: v })} />
              <ModalField label="N° da NF do Produto" testId="m-nf" value={dispatchForm.nf_produto} onChange={(v) => setDispatchForm({ ...dispatchForm, nf_produto: v })} />
              <ModalField label="N° do Pedido" testId="m-pedido" value={dispatchForm.no_pedido} onChange={(v) => setDispatchForm({ ...dispatchForm, no_pedido: v })} />
            </div>
            <div className="space-y-3">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF]">Dados do Transporte</div>
              <ModalField label="Nome do Motorista" testId="m-motorista" value={dispatchForm.motorista} onChange={(v) => setDispatchForm({ ...dispatchForm, motorista: v })} />
              <ModalField label="CPF do Motorista" testId="m-cpf" value={dispatchForm.cpf_motorista} onChange={(v) => setDispatchForm({ ...dispatchForm, cpf_motorista: v })} />
              <ModalField label="Placa do Caminhão / Carreta" testId="m-placa" value={dispatchForm.placa} onChange={(v) => setDispatchForm({ ...dispatchForm, placa: v.toUpperCase() })} />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setOpenModal(false)} className="rounded-none border-border">Cancelar</Button>
            <Button onClick={despachar} disabled={busy} data-testid="m-confirm" className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider h-11 px-6">
              <CheckIcon className="w-4 h-4 mr-2" />
              {busy ? "Emitindo..." : "Confirmar e Emitir Manifesto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, mono, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${accent ? "text-[#00E5FF]" : "text-white"} text-sm mt-0.5`}>{value || "—"}</div>
    </div>
  );
}

function ModalField({ label, testId, value, onChange, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" />
    </div>
  );
}
